/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { Box, ThemeProvider, createTheme } from '@mui/material';

import FullscreenSpinner from 'modules/common/fullscreenSpinner.component';
import VoucherDetails from './voucherDetails.component';

import { COLOR_PRIMARY, COLOR_SECONDARY, COLOR_WARNING } from 'styles/theme';

import { getAuthorizationSummary, getAuthorizationSummaries } from 'modules/authorizations/store/actions/authorizations.actions';
import { getExpenseCodesByClientId, getVendorDetails, getVendors } from 'modules/authorizations/store/actions/dataManagement.actions';
import { getClientDetails, getClients, setClientId } from 'modules/clients/clients.actions';

import {
  getVoucher,
  updateVoucher,
  createVoucherLineItem,
  updateVoucherLineItem,
  deleteVoucherLineItem,
  setVoucherId,
  getExceptionsByClientId,
  pushVoucherToAcumatica,
  startOverVoucher
} from 'modules/expenses/store/expenses.actions';

import {
  getAllCountries,
  getAllCurrencies,
  getAllEnumerations,
  getAllProvinces,
  getAllSaleTypes,
  getAllWeightTypes
} from 'modules/enumerations/store/enumerationActions';

import { fetchEnumerations } from 'modules/authorizations/utils/fetchDataDetailsService';
import { showToast } from 'modules/layout/layout.actions';
import { EXPENSES_BATCH_TYPES, EXPENSES_PAYEE_TYPE, LINE_ITEM_STATUS } from 'modules/enumerations/enumerationIds';
import { isIdEqual } from 'modules/enumerations/enumerationHelpers';
import { Permissions } from 'modules/layout/permissions';
import { hasPermission } from 'modules/layout/permissionService';
import { GUIDS } from '../voucherConstants';
import { TOAST_MESSAGE_SEVERITY_ERROR } from 'modules/layout/types';
import { POST_VOUCHER_START_OVER_FAILURE } from 'modules/expenses/store/types';

const theme = createTheme({
  palette: { primary: COLOR_PRIMARY, secondary: COLOR_SECONDARY, warning: COLOR_WARNING }
});

export const VoucherStartOverTypes = {
  CHANGE_CLIENT: 3,
  CHANGE_PAYEE_TYPE: 2,
  CHANGE_PAYEE: 1
};

const VoucherDetailsView = props => {
  const {
    match,
    enumerations,
    expenseCodes,
    getVoucher,
    updateVoucher,
    createVoucherLineItem,
    updateVoucherLineItem,
    deleteVoucherLineItem,
    getAuthorizationSummary,
    getExpenseCodesByClientId,
    getVendorDetails,
    clients,
    vendors,
    authorizationSummaries,
    getAuthorizationSummaries,
    getExceptionsByClientId,
    pushVoucherToAcumatica
  } = props;

  const voucherIdParam = (match && match.params && match.params.voucherId) || '0';
  const voucherId = parseInt(voucherIdParam, 10);

  const canReadVouchers = hasPermission(Permissions.Finance.Vouchers.Read);
  const canEditVouchers = hasPermission(Permissions.Finance.Vouchers.Edit);
  const canEditFees = hasPermission(Permissions.Finance.Fees.Edit);

  const [isLoading, setIsLoading] = useState(false);
  const [voucherDetail, setVoucherDetail] = useState(null);
  const [payeeDetails, setPayeeDetails] = useState(null);
  const [authDetails, setAuthDetails] = useState(null);
  const [clientDetails, setClientDetails] = useState(null);

  const [banks, setBanks] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [vendor, setVendor] = useState(null);

  const clientsArray = useMemo(() => (Array.isArray(clients) ? clients : Object.values(clients ?? {})), [clients]);
  const [exceptions, setExceptions] = useState([]);

  const normalizeAuthSummaries = useMemo(
    () =>
      (authorizationSummaries || []).map(a => ({
        ...a,
        transfereeName: a.transfereeName || `${a.firstName || ''} ${a.lastName || ''}`.trim()
      })),
    [authorizationSummaries]
  );

  const maskAcct = n => {
    const s = String(n || '');
    const last4 = s.slice(-4);
    return last4 ? `••••${last4}` : '';
  };

  const normalizeVendorBanks = arr =>
    Array.isArray(arr)
      ? arr.map(b => ({
        id: b.bankId,
        displayName: `${b.bankName} ${maskAcct(b.accountNumber)}`.trim(),
        _raw: b,
        paymentTypeId: b.paymentTypeId
      }))
      : [];

  const normalizeAuthBanks = arr =>
    Array.isArray(arr)
      ? arr.map(b => ({
        id: b.bankId,
        displayName: b.bankNickName,
        _raw: b,
        paymentTypeId: b.paymentTypeId
      }))
      : [];

  const normalizeAddresses = arr =>
    Array.isArray(arr)
      ? arr.map(a => {
        const line1 = [a.addressLine1, a.addressLine2, a.addressLine3].filter(Boolean).join(' ');
        const line2 = [a.city, a.zipcode].filter(Boolean).join(', ');
        return {
          id: a.addressId,
          lines: [line1, line2].filter(Boolean).join('\n'),
          _raw: a
        };
      })
      : [];

  const onUpdateVoucher = useCallback(
    async payload => {
      const id = payload?.voucherInformation?.voucherId;
      if (!id) return;
      await updateVoucher(id, payload);
      const refreshed = (await getVoucher(id)).response;
      setVoucherDetail(refreshed);
      return { response: refreshed };
    },
    [updateVoucher, getVoucher]
  );

  const onSaveLineItem = useCallback(
    async (item, voucherInfoForm) => {
      if (!voucherId) return;
      await updateVoucher(voucherId, voucherInfoForm);
      const hasId = !!item?.voucherLineItemRow?.lineItemId;
      if (hasId) {
        await updateVoucherLineItem(voucherId, item.voucherLineItemRow.lineItemId, item);
      } else {
        await createVoucherLineItem(voucherId, item);
      }
      const refreshed = (await getVoucher(voucherId)).response;
      setVoucherDetail(refreshed);
      return true;
    },
    [voucherId, createVoucherLineItem, updateVoucherLineItem, getVoucher]
  );

  const onDeleteLineItem = useCallback(
    async lineItemId => {
      if (!voucherId || !lineItemId) return;
      await deleteVoucherLineItem(voucherId, lineItemId);
      const refreshed = (await getVoucher(voucherId)).response;
      setVoucherDetail(refreshed);
      return true;
    },
    [voucherId, deleteVoucherLineItem, getVoucher]
  );

  const onCopyLineItem = useCallback(
    async lineItemId => {
      if (!voucherId || !lineItemId) return;
      const itemToCopy = Array.isArray(voucherDetail?.voucherLineItems)
        ? voucherDetail.voucherLineItems.find(li => li.lineItemId === lineItemId)
        : null;
      if (!itemToCopy) return;
      const newItem = { ...itemToCopy, lineItemId: null, lineItemStatusId: LINE_ITEM_STATUS.PENDING };
      await createVoucherLineItem(voucherId, newItem);
      const refreshed = (await getVoucher(voucherId)).response;
      setVoucherDetail(refreshed);
    },
    [voucherId, createVoucherLineItem, voucherDetail, getVoucher]
  );

  const pushVoucherToAcumaticaFunc = useCallback(async () => {
    if (!voucherId) return;
    await pushVoucherToAcumatica(voucherId);
    const refreshed = (await getVoucher(voucherId)).response;
    setVoucherDetail(refreshed);
    return { response: refreshed };
  }, [voucherId, pushVoucherToAcumatica, getVoucher]);

  const updateParentVoucherAttachments = newVoucherAttachments => {
    setVoucherDetail(prevDetail => ({
      ...prevDetail,
      voucherAttachments: newVoucherAttachments
    }));
  };

  const updateParentVoucherLineAttachments = newVoucherLineAttachments => {
    setVoucherDetail(prevDetail => ({
      ...prevDetail,
      voucherLineAttachments: newVoucherLineAttachments
    }));
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!Number.isFinite(voucherId) || voucherId <= 0) {
        setVoucherDetail(null);
        setBanks([]);
        setAddresses([]);
        return;
      }
      props.setVoucherId(voucherId);
      setIsLoading(true);
      try {
        const detail = (await getVoucher(voucherId)).response;
        if (cancelled) return;
        setVoucherDetail(detail);
        const exceptions = (await getExceptionsByClientId(detail?.voucherInformation?.clientId)).response || [];
        setExceptions(exceptions);
        if (!authorizationSummaries || authorizationSummaries.length === 0) {
          await getAuthorizationSummaries();
        }

        // The Client advanced search reads from state.clients.clients, which only GET_CLIENTS populates.
        // Without this, a direct load of this route leaves the Client field and its search grid empty.
        if (!clients || Object.keys(clients).length === 0) {
          await props.getClients();
        }

        const payee = detail?.payee;
        if (isIdEqual(payee?.payeeTypeId, EXPENSES_PAYEE_TYPE.TRANSFEREE)) {
          const authId = payee.authorizationId;
          if (authId) {
            const auth = (await getAuthorizationSummary(authId)).response;
            if (cancelled) return;
            setPayeeDetails(null);
            setAuthDetails(auth ?? null);
            setBanks(normalizeAuthBanks(auth?.bankAccounts));
            setAddresses(normalizeAddresses(auth?.addresses));
            setVendor(null);
          } else {
            setPayeeDetails(null);
            setAuthDetails(null);
            setBanks([]);
            setAddresses([]);
            setVendor(null);
          }
        } else if (isIdEqual(payee?.payeeTypeId, EXPENSES_PAYEE_TYPE.VENDOR)) {
          // Only needed on vendor vouchers - the Vendor advanced search grid binds to this list.
          if (!vendors || vendors.length === 0) {
            await props.getVendors();
          }
          const vendorId = payee.vendorId;
          if (vendorId) {
            const vendor = (await getVendorDetails(vendorId)).response;
            if (cancelled) return;
            setPayeeDetails(vendor);
            setAuthDetails(null);
            setBanks(normalizeVendorBanks(vendor?.banks));
            setAddresses(normalizeAddresses(vendor?.addresses));
            setVendor(vendor);
          } else {
            setPayeeDetails(null);
            setAuthDetails(null);
            setBanks([]);
            setAddresses([]);
          }
        } else {
          setPayeeDetails(null);
          setAuthDetails(null);
          setBanks([]);
          setAddresses([]);
        }

        await fetchEnumerations(props);
        const clientRecordId = detail?.voucherInformation?.clientId;
        let expenseCodeClientId = null;
        if (clientRecordId) {
          props.setClientId(clientRecordId);
          const clientResp = await props.getClientDetails(clientRecordId);
          if (cancelled) return;
          setClientDetails(clientResp?.response ?? null);
          expenseCodeClientId = clientResp?.response?.clientDetails?.clientId || clientResp?.response?.clientId || null;
        }
        if (expenseCodeClientId) {
          await getExpenseCodesByClientId(expenseCodeClientId);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [voucherId]);

  if (!canReadVouchers) {
    props.history.push('/');
    return null;
  }

  const handleStartOver = async (clientId, payeeType, payeeId, typeOfStartOver) => {
    try {
      let request = {
        operationType: typeOfStartOver,
        voucherId: voucherId,
        clientRecordId: clientId,
        newPayToId: payeeType,
        vendorId: payeeType === GUIDS.PAYEE_TYPE.VENDOR ? payeeId : null,
        authorizationId: payeeType === GUIDS.PAYEE_TYPE.TRANSFEREE ? payeeId : null
      }

      const resp = await props.startOverVoucher(voucherId, request);
      if (resp.type === POST_VOUCHER_START_OVER_FAILURE) {
        props.showToast('Failed to start over voucher', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
        return;
      }

      let response = resp?.response ?? null;
      if (!response) {
        props.showToast('Failed to start over voucher', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
        return;
      }

      let voucherInfo = response.voucherInformation;
      let newVoucherId = voucherInfo?.voucherId || null;
      if (newVoucherId !== voucherId) {
        props.history.push(`/vouchers/${newVoucherId}`);
        return;
      }
      const detail = (await getVoucher(newVoucherId)).response;
      const exceptions = (await getExceptionsByClientId(detail?.voucherInformation?.clientId)).response || [];
      setExceptions(exceptions);

      const payee = detail?.payee;
      if (isIdEqual(payee?.payeeTypeId, EXPENSES_PAYEE_TYPE.TRANSFEREE)) {
        const authId = payee.authorizationId;
        if (authId) {
          const auth = (await getAuthorizationSummary(authId)).response;
          setPayeeDetails(null);
          setAuthDetails(auth ?? null);
          setBanks(normalizeAuthBanks(auth?.bankAccounts));
          setAddresses(normalizeAddresses(auth?.addresses));
          setVendor(null);
        } else {
          setPayeeDetails(null);
          setAuthDetails(null);
          setBanks([]);
          setAddresses([]);
          setVendor(null);
        }
      } else if (isIdEqual(payee?.payeeTypeId, EXPENSES_PAYEE_TYPE.VENDOR)) {
        // Only needed on vendor vouchers - the Vendor advanced search grid binds to this list.
        if (!vendors || vendors.length === 0) {
          await props.getVendors();
        }
        const vendorId = payee.vendorId;
        if (vendorId) {
          const vendor = (await getVendorDetails(vendorId)).response;
          setPayeeDetails(vendor);
          setAuthDetails(null);
          setBanks(normalizeVendorBanks(vendor?.banks));
          setAddresses(normalizeAddresses(vendor?.addresses));
          setVendor(vendor);
        } else {
          setPayeeDetails(null);
          setAuthDetails(null);
          setBanks([]);
          setAddresses([]);
        }
      } else {
        setPayeeDetails(null);
        setAuthDetails(null);
        setBanks([]);
        setAddresses([]);
      }
      setVoucherDetail(detail);

      return;

    } finally {
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box>
        {!isLoading && voucherDetail && enumerations !== undefined && clients !== undefined && (
          <VoucherDetails
            voucherDetail={voucherDetail}
            voucherId={voucherId}
            expenseCodes={expenseCodes}
            enumerations={enumerations.allEnums || []}
            banks={banks}
            addresses={addresses}
            payeeDetails={payeeDetails}
            authDetails={authDetails}
            clientRelocationPolicies={clientDetails?.clientRelocationPolicies ?? []}
            clients={clientsArray}
            onUpdateVoucher={onUpdateVoucher}
            onSaveLineItem={onSaveLineItem}
            onDeleteLineItem={onDeleteLineItem}
            onCopyLineItem={onCopyLineItem}
            history={props.history}
            location={props.location}
            match={props.match}
            normalizeAuthSummaries={normalizeAuthSummaries}
            updateParentVoucherAttachments={updateParentVoucherAttachments}
            updateParentVoucherLineAttachments={updateParentVoucherLineAttachments}
            exceptions={exceptions}
            clientId={voucherDetail?.voucherInformation?.clientId}
            setExceptions={setExceptions}
            vendor={vendor}
            pushVoucherToAcumaticaFunc={pushVoucherToAcumaticaFunc}
            isReadOnly={
              isIdEqual(voucherDetail?.voucherInformation?.batchTypeId, EXPENSES_BATCH_TYPES.FEE)
                ? !canEditFees
                : !canEditVouchers
            }
            handleStartOver={handleStartOver}
            authorizationSummaries={authorizationSummaries}
          />
        )}
        {isLoading && <FullscreenSpinner />}
      </Box>
    </ThemeProvider>
  );
};

const mapStateToProps = state => {
  const { authorizationDetails, expenseCodes, authorizationSummaries, vendors } = state.authorizations || {};
  const enumerations = state.enumerations;
  const { clients } = state.clients;

  return {
    authorizationDetails,
    expenseCodes,
    enumerations,
    clients,
    vendors,
    authorizationSummaries
  };
};

export default compose(
  connect(mapStateToProps, {
    getVoucher,
    updateVoucher,
    createVoucherLineItem,
    updateVoucherLineItem,
    deleteVoucherLineItem,
    getAuthorizationSummary,
    getAllEnumerations,
    getExpenseCodesByClientId,
    getClientDetails,
    getClients,
    setClientId,
    getAllCountries,
    getAllCurrencies,
    getAllProvinces,
    getAllSaleTypes,
    getAllWeightTypes,
    showToast,
    getVendorDetails,
    getVendors,
    getAuthorizationSummaries,
    setVoucherId,
    getExceptionsByClientId,
    pushVoucherToAcumatica,
    startOverVoucher
  })
)(VoucherDetailsView);
